import { ReactNode, useMemo } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import {
  addMonths,
  compareISO,
  dayBadge,
  formatMonthLabel,
  formatMonthRange,
  monthOf,
} from '@backrs_ui/common-ui/src/utils/dateTime.util';

import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';

import {
  CalendarDay,
  CalendarEntry,
  DayStatus,
  EntryTone,
  KindVisual,
  MonthCalendarProps,
} from './monthCalendar.types';
import { deriveStatus, getMonthGrid } from './monthGrid.util';

const STRINGS = appStrings.modules.monthCalendar;

type StatusVisual = Readonly<{ icon?: AppIcons; iconColor?: ColorToken }>;

const STATUS_VISUALS: Record<DayStatus, StatusVisual> = {
  upcoming: {},
  due: {},
  overdue: { icon: 'AlertCircle', iconColor: 'Colors/Foreground/fg-error-primary' },
  completed: { icon: 'CheckCircle', iconColor: 'Colors/Foreground/fg-success-primary' },
};

type ToneVisual = Readonly<{ bg: ColorToken; fg: ColorToken; border: ColorToken; dot: ColorToken }>;

const TONE_VISUALS: Record<EntryTone, ToneVisual> = {
  brand: { bg: 'Brand/50', fg: 'Brand/700', border: 'Brand/200', dot: 'Brand/500' },
  indigo: { bg: 'Indigo/50', fg: 'Indigo/700', border: 'Indigo/200', dot: 'Indigo/500' },
  blue: { bg: 'Blue/50', fg: 'Blue/700', border: 'Blue/200', dot: 'Blue/500' },
  orange: { bg: 'Orange/50', fg: 'Orange/700', border: 'Orange/200', dot: 'Orange/500' },
  teal: { bg: 'Teal/50', fg: 'Teal/700', border: 'Teal/200', dot: 'Teal/500' },
  pink: { bg: 'Pink/50', fg: 'Pink/700', border: 'Pink/200', dot: 'Pink/500' },
  green: { bg: 'Green/50', fg: 'Green/700', border: 'Green/200', dot: 'Green/500' },
  yellow: { bg: 'Yellow/50', fg: 'Yellow/700', border: 'Yellow/200', dot: 'Yellow/500' },
  neutral: {
    bg: 'Colors/Background/bg-secondary',
    fg: 'Colors/Text/text-secondary',
    border: 'Colors/Border/border-secondary',
    dot: 'Colors/Foreground/fg-secondary',
  },
};

/** Entries beyond this collapse into a "+N more" line. */
const MAX_VISIBLE_ENTRIES = 2;

/** Full label for wide layouts, single-letter for the narrow mobile grid. */
const WEEKDAYS: readonly { full: string; short: string }[] = [
  { full: 'Mon', short: 'Mon' },
  { full: 'Tues', short: 'T' },
  { full: 'Wed', short: 'W' },
  { full: 'Thu', short: 'T' },
  { full: 'Fri', short: 'F' },
  { full: 'Sat', short: 'S' },
  { full: 'Sun', short: 'S' },
];

/** Whether `value` parses to a real date AND round-trips through `pattern` unchanged. */
function matchesDatePattern(value: string, pattern: string): boolean {
  const parsed = parseISO(value);

  return isValid(parsed) && format(parsed, pattern) === value;
}

type DateProps = Pick<MonthCalendarProps, 'visibleMonth' | 'today' | 'startDate' | 'days'>;

/** A message describing the first malformed date prop, or null when all are well-formed. */
function findInvalidDateProp({ visibleMonth, today, startDate, days }: DateProps): string | null {
  if (!matchesDatePattern(visibleMonth, 'yyyy-MM')) {
    return `visibleMonth "${visibleMonth}" is not a valid YYYY-MM month`;
  }
  if (!matchesDatePattern(today, 'yyyy-MM-dd')) {
    return `today "${today}" is not a valid YYYY-MM-DD date`;
  }
  if (!matchesDatePattern(startDate, 'yyyy-MM-dd')) {
    return `startDate "${startDate}" is not a valid YYYY-MM-DD date`;
  }

  const badDay = days.find((day) => !matchesDatePattern(day.date, 'yyyy-MM-dd'));

  return badDay ? `days[].date "${badDay.date}" is not a valid YYYY-MM-DD date` : null;
}

export function MonthCalendar({
  id = 'month-calendar',
  startDate,
  today,
  days,
  visibleMonth,
  onMonthChange,
  onDayClick,
  headerAction,
  emptyState,
}: MonthCalendarProps) {
  const invalidDateProp = useMemo(() => {
    const message = findInvalidDateProp({ visibleMonth, today, startDate, days });

    if (message) {
      console.warn(`[MonthCalendar] ${message} — rendering the empty state instead.`);
    }

    return message;
  }, [visibleMonth, today, startDate, days]);

  const grid = useMemo(
    () => (invalidDateProp ? [] : getMonthGrid(visibleMonth)),
    [invalidDateProp, visibleMonth],
  );
  const dayByISO = useMemo(() => {
    const map = new Map<string, CalendarDay>();

    days.forEach((day) => map.set(day.date, day));

    return map;
  }, [days]);

  if (invalidDateProp) {
    return (
      <Box id={id} sx={rootSx}>
        <Typography variant="text-sm-regular" sx={emptyStateSx}>
          {emptyState}
        </Typography>
      </Box>
    );
  }

  const isEmpty = days.length === 0;

  return (
    <Box id={id} sx={rootSx}>
      <CalendarHeader
        id={id}
        title={formatMonthLabel(visibleMonth)}
        range={formatMonthRange(visibleMonth)}
        badge={dayBadge(today)}
        onPrev={() => onMonthChange(addMonths(visibleMonth, -1))}
        onNext={() => onMonthChange(addMonths(visibleMonth, 1))}
        onToday={() => onMonthChange(monthOf(today))}
        headerAction={headerAction}
      />

      <Box sx={gridShellSx} role="grid">
        {WEEKDAYS.map((weekday, index) => (
          <Box key={index} sx={weekdayHeadSx} role="columnheader">
            <Typography variant="text-xs-medium" sx={weekdayFullSx}>
              {weekday.full}
            </Typography>
            <Typography variant="text-xs-medium" sx={weekdayShortSx}>
              {weekday.short}
            </Typography>
          </Box>
        ))}

        {grid.map((cell) => {
          const day = dayByISO.get(cell.iso);
          const isToday = cell.iso === today;

          if (!day) {
            return (
              <Box key={cell.iso} sx={inertCellSx} role="gridcell">
                <DayNumber value={cell.dayOfMonth} isToday={isToday} muted />
              </Box>
            );
          }

          const status = deriveStatus(day, today);
          const isPast = compareISO(cell.iso, today) < 0;

          return (
            <DayCell
              key={cell.iso}
              id={`${id}-day-${cell.iso}`}
              dayOfMonth={cell.dayOfMonth}
              day={day}
              status={status}
              isToday={isToday}
              dimmed={isPast && status !== 'overdue'}
              outOfMonth={!cell.inCurrentMonth}
              onClick={() => {
                // Follow a borrowed day into its own month, so the grid matches the selection.
                if (!cell.inCurrentMonth) onMonthChange(monthOf(cell.iso));

                onDayClick(cell.iso);
              }}
            />
          );
        })}
      </Box>

      {isEmpty ? (
        <Typography variant="text-sm-regular" sx={emptyStateSx}>
          {emptyState}
        </Typography>
      ) : null}
    </Box>
  );
}

type CalendarHeaderProps = Readonly<{
  id: string;
  title: string;
  range: string;
  badge: { month: string; day: number };
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  headerAction?: ReactNode;
}>;

function CalendarHeader({
  id,
  title,
  range,
  badge,
  onPrev,
  onNext,
  onToday,
  headerAction,
}: CalendarHeaderProps) {
  return (
    <Box sx={headerSx}>
      <Box sx={headerLeftSx}>
        <Box sx={badgeBoxSx} aria-hidden>
          <Typography variant="text-xs-semibold" sx={badgeMonthSx}>
            {badge.month}
          </Typography>
          <Typography variant="text-lg-bold" sx={badgeDaySx}>
            {badge.day}
          </Typography>
        </Box>
        <Box sx={titleBlockSx}>
          <Typography variant="text-lg-bold" sx={titleSx}>
            {title}
          </Typography>
          <Typography variant="text-sm-regular" sx={rangeSx}>
            {range}
          </Typography>
        </Box>
      </Box>

      <Box sx={headerNavSx}>
        <Box sx={navGroupSx}>
          <IconButton
            id={`${id}-prev-month`}
            aria-label={STRINGS.previousMonth}
            onClick={onPrev}
            sx={navButtonSx}
          >
            <AppIcon
              iconName="ChevronLeft"
              diameter="20px"
              color="Colors/Foreground/fg-secondary"
            />
          </IconButton>
          <Box
            component="button"
            id={`${id}-today`}
            type="button"
            onClick={onToday}
            sx={todayButtonSx}
          >
            <Typography variant="text-sm-semibold" sx={todayButtonTextSx}>
              {STRINGS.today}
            </Typography>
          </Box>
          <IconButton
            id={`${id}-next-month`}
            aria-label={STRINGS.nextMonth}
            onClick={onNext}
            sx={navButtonSx}
          >
            <AppIcon
              iconName="ChevronRight"
              diameter="20px"
              color="Colors/Foreground/fg-secondary"
            />
          </IconButton>
        </Box>
        {headerAction}
      </Box>
    </Box>
  );
}

type DayNumberProps = Readonly<{ value: number; isToday: boolean; muted?: boolean }>;

function DayNumber({ value, isToday, muted }: DayNumberProps) {
  if (isToday) {
    return (
      <Box sx={todayBadgeSx}>
        <Typography variant="text-xs-semibold" sx={todayBadgeTextSx}>
          {value}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography variant="text-sm-medium" sx={muted ? inertNumberSx : dayNumberSx}>
      {value}
    </Typography>
  );
}

type DayCellProps = Readonly<{
  id: string;
  dayOfMonth: number;
  day: CalendarDay;
  status: DayStatus;
  isToday: boolean;
  dimmed: boolean;
  /** Borrowed from an adjacent month; renders on the inert cells' lighter ground. */
  outOfMonth: boolean;
  onClick: () => void;
}>;

function DayCell({
  id,
  dayOfMonth,
  day,
  status,
  isToday,
  dimmed,
  outOfMonth,
  onClick,
}: DayCellProps) {
  const kind = day.kindVisual;
  const statusVisual = STATUS_VISUALS[status] ?? STATUS_VISUALS.upcoming;
  // Days without a kind pill (e.g. content days) are announced by their entries instead.
  const kindLabel = kind?.label ?? day.entries?.map((entry) => entry.label).join(', ') ?? '';

  return (
    <Box
      component="button"
      id={id}
      type="button"
      onClick={onClick}
      aria-label={STRINGS.dayAriaLabel(
        kindLabel,
        dayOfMonth,
        status,
        outOfMonth ? dayBadge(day.date).month : undefined,
      )}
      sx={dayCellSx(dimmed, outOfMonth)}
    >
      <Box sx={cellTopRowSx}>
        <DayNumber value={dayOfMonth} isToday={isToday} />
        {statusVisual.icon && statusVisual.iconColor ? (
          <AppIcon iconName={statusVisual.icon} diameter="16px" color={statusVisual.iconColor} />
        ) : null}
      </Box>

      {kind ? (
        <Box sx={kindPillSx(kind)}>
          {kind.icon ? <AppIcon iconName={kind.icon} diameter="12px" color="currentColor" /> : null}
          <Typography variant="text-xs-semibold" sx={kindPillTextSx} noWrap>
            {kind.label}
          </Typography>
        </Box>
      ) : null}

      {day.entries && day.entries.length > 0 ? (
        <EntryPills entries={day.entries} completed={day.completed} />
      ) : null}

      {day.focus ? (
        <Typography variant="text-xs-medium" sx={focusChipSx} noWrap>
          {day.focus}
        </Typography>
      ) : null}

      {day.note ? (
        <Typography variant="text-xs-medium" sx={noteChipSx} noWrap title={day.note}>
          {day.note}
        </Typography>
      ) : null}
    </Box>
  );
}

/** `completed` suppresses the leading dots: the dot marks a day still owing work. */
function EntryPills({
  entries,
  completed,
}: {
  entries: readonly CalendarEntry[];
  completed: boolean;
}) {
  const visible = entries.slice(0, MAX_VISIBLE_ENTRIES);
  const overflow = entries.length - visible.length;

  return (
    <Box sx={entryListSx}>
      {visible.map((entry, index) => {
        const tone = TONE_VISUALS[entry.tone] ?? TONE_VISUALS.neutral;

        return (
          <Box key={`${entry.label}-${index}`} sx={entryPillSx(tone)}>
            {!completed && <Box sx={entryDotSx(tone)} aria-hidden />}
            <Typography variant="text-xs-semibold" sx={entryPillTextSx}>
              {entry.label}
            </Typography>
          </Box>
        );
      })}
      {overflow > 0 ? (
        <Typography variant="text-xs-medium" sx={moreEntriesSx} noWrap>
          {STRINGS.moreEntries(overflow)}
        </Typography>
      ) : null}
    </Box>
  );
}

const MOBILE = '@media (max-width: 600px)';

const rootSx: SxProps<Theme> = () => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
  boxSizing: 'border-box',
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
  padding: backrs.spacing['spacing-2xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  [MOBILE]: { padding: backrs.spacing['spacing-lg'] },
});

const headerLeftSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const badgeBoxSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '48px',
  padding: backrs.spacing['spacing-xs'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
});

const badgeMonthSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  textTransform: 'uppercase',
  lineHeight: 1.2,
});

const badgeDaySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  lineHeight: 1.1,
});

const titleBlockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const rangeSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const headerNavSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

// The group owns the outline so prev/today/next read as one segmented control.
const navGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'stretch',
  overflow: 'hidden',
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const navButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-xs'],
  borderRadius: 0,
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&:last-of-type': { borderRight: 'none' },
});

const todayButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']}`,
  borderRadius: 0,
  border: 'none',
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  cursor: 'pointer',
  font: 'inherit',
  '&:hover': { backgroundColor: backrs.colors['Colors/Background/bg-secondary'] },
  '&:focus-visible': {
    outline: `2px solid ${backrs.colors['Colors/Foreground/fg-brand-primary']}`,
    outlineOffset: '-2px',
  },
});

const todayButtonTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

// A 1px grid gap over a border-colored background paints the cell separators,
// so tracks never overflow their container (no clipped last column).
const gridShellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  // First row (weekday heads) is content-sized; day rows stretch to fill the card height.
  gridTemplateRows: 'auto',
  gridAutoRows: 'minmax(112px, 1fr)',
  gap: '1px',
  flex: 1,
  minHeight: 0,
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: backrs.colors['Colors/Border/border-secondary'],
  [MOBILE]: {
    gridAutoRows: 'minmax(60px, 1fr)',
  },
});

const weekdayHeadSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 0,
  padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-sm']}`,
  textAlign: 'center',
  color: backrs.colors['Colors/Text/text-tertiary'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const weekdayFullSx: SxProps<Theme> = { color: 'inherit', [MOBILE]: { display: 'none' } };

const weekdayShortSx: SxProps<Theme> = {
  color: 'inherit',
  display: 'none',
  [MOBILE]: { display: 'block' },
};

const inertCellSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: 0,
  minHeight: '112px',
  padding: backrs.spacing['spacing-sm'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-subtle'],
  boxSizing: 'border-box',
  [MOBILE]: { minHeight: '60px', padding: backrs.spacing['spacing-xs'] },
});

const dayCellSx =
  (dimmed: boolean, outOfMonth: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xs'],
    minWidth: 0,
    minHeight: '112px',
    padding: backrs.spacing['spacing-sm'],
    textAlign: 'left',
    boxSizing: 'border-box',
    overflow: 'hidden',
    backgroundColor: outOfMonth
      ? backrs.colors['Colors/Background/bg-secondary-subtle']
      : backrs.colors['Colors/Background/bg-primary'],
    color: backrs.colors['Colors/Text/text-primary'],
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
    opacity: dimmed ? 0.55 : 1,
    transition: 'background-color 0.1s ease',
    '&:hover': { backgroundColor: backrs.colors['Colors/Background/bg-active'] },
    '&:focus-visible': {
      outline: `2px solid ${backrs.colors['Colors/Foreground/fg-brand-primary']}`,
      outlineOffset: '-2px',
    },
    [MOBILE]: {
      minHeight: '60px',
      padding: backrs.spacing['spacing-xs'],
      gap: backrs.spacing['spacing-xxs'],
    },
  });

const cellTopRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '24px',
};

const dayNumberSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const inertNumberSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const todayBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Colors/Background/bg-brand-solid'],
  [MOBILE]: { width: '20px', height: '20px' },
});

const todayBadgeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-white'],
});

const kindPillSx =
  (kind: KindVisual): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-xs'],
    minWidth: 0,
    width: '104px',
    padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']}`,
    borderRadius: backrs.radius['radius-sm'],
    border: kind.border ? `1px solid ${backrs.colors[kind.border]}` : 'none',
    backgroundColor: backrs.colors[kind.bg],
    color: backrs.colors[kind.fg],
    maxWidth: '100%',
    boxSizing: 'border-box',
    // Narrow widths collapse the labelled pill to a single colored dot.
    [MOBILE]: {
      width: '8px',
      height: '8px',
      padding: 0,
      borderRadius: backrs.radius['radius-full'],
      backgroundColor: backrs.colors[kind.dot],
      '& > *': { display: 'none' },
    },
  });

const kindPillTextSx: SxProps<Theme> = { color: 'inherit', minWidth: 0 };

const focusChipSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: 'auto',
  padding: `0 ${backrs.spacing['spacing-xs']}`,
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  color: backrs.colors['Colors/Text/text-tertiary'],
  alignSelf: 'flex-start',
  maxWidth: '100%',
  [MOBILE]: { display: 'none' },
});

const noteChipSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: 'auto',
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontStyle: 'italic',
  alignSelf: 'flex-start',
  maxWidth: '100%',
  [MOBILE]: { display: 'none' },
});

const entryListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  minWidth: 0,
  // Narrow widths collapse each pill to its leading dot (mirrors the kind-pill behavior).
  [MOBILE]: { flexDirection: 'row', flexWrap: 'wrap', gap: backrs.spacing['spacing-xxs'] },
});

const entryPillSx =
  (tone: ToneVisual): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-xs'],
    minWidth: 0,
    width: '104px',
    maxWidth: '100%',
    padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']}`,
    borderRadius: backrs.radius['radius-sm'],
    border: `1px solid ${backrs.colors[tone.border]}`,
    backgroundColor: backrs.colors[tone.bg],
    color: backrs.colors[tone.fg],
    boxSizing: 'border-box',
    [MOBILE]: {
      width: '8px',
      height: '8px',
      padding: 0,
      borderRadius: backrs.radius['radius-full'],
      backgroundColor: backrs.colors[tone.dot],
      '& > *:not([aria-hidden])': { display: 'none' },
      '& > [aria-hidden]': { display: 'none' },
    },
  });

// Long category names wrap in one narrow cell, so the label runs tighter than the xs default.
const ENTRY_PILL_LINE_HEIGHT = '1rem';
const ENTRY_DOT_SIZE = '6px';

const entryDotSx =
  (tone: ToneVisual): SxProps<Theme> =>
  ({ backrs }) => ({
    flexShrink: 0,
    // Centers the dot on the label's first line.
    marginTop: `calc((${ENTRY_PILL_LINE_HEIGHT} - ${ENTRY_DOT_SIZE}) / 2)`,
    width: ENTRY_DOT_SIZE,
    height: ENTRY_DOT_SIZE,
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: backrs.colors[tone.dot],
  });

const entryPillTextSx: SxProps<Theme> = {
  color: 'inherit',
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  lineHeight: ENTRY_PILL_LINE_HEIGHT,
};

const moreEntriesSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  paddingLeft: backrs.spacing['spacing-xxs'],
  [MOBILE]: { display: 'none' },
});

const emptyStateSx: SxProps<Theme> = ({ backrs }) => ({
  textAlign: 'center',
  color: backrs.colors['Colors/Text/text-tertiary'],
  padding: backrs.spacing['spacing-3xl'],
});
